import { useEffect, useState } from "react";
import { Calculator, Save, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorText } from "@/lib/api";
import { fmtIdr } from "@/lib/format";
import { toast } from "sonner";

export const FxBudgetRateDialog = ({ open, onOpenChange, budgetRates, defaultYear, onSaved }) => {
  const [year, setYear] = useState(defaultYear);
  const [rate, setRate] = useState("");
  const [note, setNote] = useState("");
  const [avg, setAvg] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    const cur = budgetRates?.[String(year)];
    setRate(cur ? cur.rate : "");
    setNote(cur ? cur.note || "" : "");
  }, [open, year, budgetRates]);

  const useAverage = async () => {
    setBusy("avg");
    try {
      const r = await api.get("/fx/average", { params: { to: "IDR" } });
      setAvg(r.data);
      setRate(Math.round(r.data.average));
      if (!note) setNote(`12-month average ${r.data.start} → ${r.data.end} (ECB via Frankfurter)`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    if (!Number(rate) || Number(rate) <= 0) { toast.error("Enter a positive rate"); return; }
    setBusy("save");
    try {
      await api.put("/fx/budget-rate", { year: Number(year), rate: Number(rate), note });
      toast.success(`Budget FX rate ${year} saved`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove budget FX rate for ${year}?`)) return;
    setBusy("del");
    try {
      await api.delete(`/fx/budget-rate/${year}`);
      toast.success("Budget FX rate removed");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="fx-budget-rate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Budget FX rate (USD/IDR)</DialogTitle>
          <DialogDescription>Fixed company rate used for budget assumptions — separate from the live ECB rate.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[110px_1fr] gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Budget year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="font-mono" data-testid="fx-budget-year-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Rate (IDR per 1 USD)</Label>
            <Input type="number" step="1" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 16250" className="font-mono" data-testid="fx-budget-rate-input" />
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-1" data-testid="fx-budget-rate-preview">{rate ? fmtIdr(rate) : "—"} per USD</p>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Board-approved planning rate" data-testid="fx-budget-note-input" />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={useAverage} disabled={!!busy} data-testid="fx-use-average-button" className="justify-start">
          <Calculator size={14} className="mr-2" /> {busy === "avg" ? "Fetching…" : "Use 12-month average USD/IDR"}
        </Button>
        {avg && <p className="text-[11px] text-slate-500" data-testid="fx-average-info">Average {fmtIdr(avg.average)} over {avg.days} ECB days ({avg.start} → {avg.end}); range {fmtIdr(avg.min)} – {fmtIdr(avg.max)}.</p>}
        <div className="flex justify-between gap-2 pt-2">
          {budgetRates?.[String(year)] ? <Button type="button" variant="ghost" size="sm" onClick={remove} disabled={!!busy} data-testid="fx-budget-rate-delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} className="mr-1" /> Remove</Button> : <span />}
          <Button type="button" onClick={save} disabled={!!busy} data-testid="fx-budget-rate-save" className="bg-slate-900 hover:bg-emerald-700 text-white"><Save size={14} className="mr-2" /> {busy === "save" ? "Saving…" : "Save rate"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
